/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isNull from 'lodash.isnull';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {getUserTimeZoneOffset} from '@ion/core/src/timezone/timezone';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {
  getCurrentDate,
  getYesterday,
  timeDiff,
  toTimeZoneUtcOffset
} from '@ion/core/src/utils/DateUtil';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {EventTypeChooser} from '@ion/features/src/entities/events/EventTypeChooser';
import {ExportContext} from '../../types/redux';
import EventsGrid from './EventsGrid';
import {
  adminDevicesViewEvents,
  listEventObj,
  listOfMsgType
} from './ViewDeviceEditorUtils';

export interface EventsTabProps {
  selectedTab: Number;
  tabIndex: Number;
  device?: Device;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display                        : 'flex',
    marginTop                      : '20px',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      flexWrap : 'wrap'
    }
  },
  dropdownStyle : {
    width                          : '200px',
    marginRight                    : '10px',
    [theme.breakpoints.down('sm')] : {
      width       : '48%',
      marginRight : '5px'
    }
  },
  eventDropdownStyle : {
    width                          : '200px',
    [theme.breakpoints.down('sm')] : {
      width : '170px'
    }
  },
  searchContainer : {
    marginRight : '10px',
    marginTop   : '26px'
  },
  iconStyle : {
    display     : 'flex',
    marginLeft  : 'auto',
    marginRight : '10px',
    marginTop   : '25px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    padding       : '5px 5px 5px 10px',
    textTransform : 'capitalize'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color : dlsColor('aaBlue')
  },
  buttonStyle : {
    marginTop : '25px'
  },
  buttonGroup : {
    height : '30px'
  },
  gridContainer : {
    margin : '20px 10px 20px 0',
    height : 'calc(100vh - 434px)'
  },
  detailsTab : {
    height : '100%'
  }
}));

/**
 * EventsTab component that displays the Device Events
 * data for the selected Device Details tab.
 */
export const EventsTab = ({
  device,
  tabIndex = 0,
  selectedTab = 0
}: EventsTabProps) => {
  const allLabel   = translate('common:features.eventType.allLabel');
  const dateFromat = 'YYYY-MM-DDTHH:mm:ss.000[Z]';

  const classes                             = useStyles();
  const gridRef                             = useRef<GenericObject>();
  const {pending}                           = useSelector(({exporter}) => exporter.current);
  const isExporting                         = pending[ExportContext.eventHistory] === true;
  const [startDate, setStartDate]           = useState(null);
  const [endDate, setEndDate]               = useState(null);
  const [eventTime, setEventTime]           = useState(null);
  const [isValidDates, setIsValidDates]     = useState<boolean>(true);
  const [showErrorSnack, setShowErrorSnack] = useState<boolean>(false);
  const [eventType, setEventType]           = useState(null);
  const [refreshGrid, setRefreshGrid]       = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const onEventTypeChange = useCallback(function(value) {
    const listEventsArray = Object.keys(listEventObj);
    let selEvent          = listEventsArray.indexOf(value) > -1 ? listEventObj[value] : value;
    selEvent              = selEvent?.replace(/ /g, '');
    setEventType(listOfMsgType[selEvent]);
  }, []);

  const toggleDatePicker = useCallback(function() {
    setOpenDatePicker(true);
    setTimeout(() => {
      setOpenDatePicker(false);
    }, 1000);
  }, []);

  const onExport = useCallback(function() {
    gridRef.current?.onExport();
  }, []);

  const refreshData = useCallback(function() {
    setRefreshGrid(true);
    setTimeout(() => {
      setRefreshGrid(false);
    }, 500);
  }, [device]);

  useEffect(() => {
    if (!isNull(startDate) && !isNull(endDate)) {
      const daysDiff           = timeDiff(endDate, startDate);
      const isValid            = daysDiff >= 0 && daysDiff <= 7;
      const userTimeZoneOffset = getUserTimeZoneOffset();
      setIsValidDates(isValid);
      if (isValid) {
        const startDateTime             = moment(startDate).startOf('day').format(dateFromat);
        const endDateTime               = moment(endDate).endOf('day').format(dateFromat);
        const userTimeZoneStartDateTime = toTimeZoneUtcOffset(startDateTime, -userTimeZoneOffset, dateFromat);
        const userTimeZoneEndDateTime   = toTimeZoneUtcOffset(endDateTime, -userTimeZoneOffset, dateFromat);
        const eventTime                 = `[${userTimeZoneStartDateTime} TO ${userTimeZoneEndDateTime}]`;
        setEventTime(eventTime);
      }
    }
  }, [startDate, endDate]);

  const OnChangeStartDate = useCallback((value) => {
    setStartDate(value);
  }, []);
  const OnChangeEndDate = useCallback((value) => {
    setEndDate(value);
  }, []);

  useEffect(() => {
    refreshData();
  }, [eventType, device]);

  const onSearch = useCallback(function() {
    if (!isValidDates) {
      setShowErrorSnack(true);
    } else {
      refreshData();
    };
  }, [isValidDates, eventTime]);

  const onCloseSnack = useCallback(() => {
    setShowErrorSnack(false);
  }, []);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab} hidden={selectedTab !== tabIndex}>
      <div className={classes.flexStyle}>
        <div className={classes.dropdownStyle}>
          <DatePicker closeOnClickOutside
            useDatePickerTextField
            disableFuture
            hideClearButton
            open={openDatePicker}
            fieldLabel={translate(`${adminDevicesViewEvents}.startDate`)}
            hasError={showErrorSnack}
            toggle={toggleDatePicker}
            defaultValue={getYesterday().fromDate}
            onChange={OnChangeStartDate}/>
        </div>
        <div className={classes.dropdownStyle}>
          <DatePicker closeOnClickOutside
            useDatePickerTextField
            disableFuture
            hideClearButton
            open={openDatePicker}
            fieldLabel={translate(`${adminDevicesViewEvents}.endDate`)}
            hasError={showErrorSnack}
            toggle={toggleDatePicker}
            defaultValue={getCurrentDate()}
            onChange={OnChangeEndDate}/>
        </div>
        <div className={classes.searchContainer}>
          <PrimaryButton onClick={onSearch} >
            {translate(`${adminDevicesViewEvents}.searchButton`)}
          </PrimaryButton>
        </div>
        <div className={classnames(classes.eventDropdownStyle, classes.searchContainer)}>
          <EventTypeChooser includeNone
            noneOptionValue='all'
            noneOptionLabel={allLabel}
            displayLabel={false}
            deviceId={device?.id}
            defaultValue={eventType || allLabel}
            onChange={function(value) {
              if (value?.name) {
                onEventTypeChange(value?.name);
              } else {
                setEventType(null);
              }
            }} />
        </div>
        <div className={classes.iconStyle}>
          <CSVExportButton onExport={onExport} isExporting={isExporting} />
          <RefreshGridButton onRefresh={refreshData} />
        </div>
      </div>
      <div className={classes.gridContainer}>
        <EventsGrid device={device}
          ref={gridRef}
          refreshGrid={refreshGrid}
          eventTime={eventTime}
          eventType={eventType} />
      </div>
      {showErrorSnack && (
        <IonSnackbar show
          customDismissLabel={translate(`${adminDevicesViewEvents}.dismiss`)}
          message={translate(`${adminDevicesViewEvents}.dateError`)}
          onDismiss={onCloseSnack} />
      )}
    </div>
  );
};

export default EventsTab;
